import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { ReactComponent as AngrySvg } from 'src/assets/svg/Angry.svg'
import { ReactComponent as CareSvg } from 'src/assets/svg/Care.svg'
import { ReactComponent as ChevronSvg } from 'src/assets/svg/chevron.svg'
import { ReactComponent as GlobeSvg } from 'src/assets/svg/Globe.svg'
import { ReactComponent as HahaSvg } from 'src/assets/svg/Haha.svg'
import { ReactComponent as HeartSvg } from 'src/assets/svg/Heart.svg'
import { ReactComponent as LikeSvg } from 'src/assets/svg/Like.svg'
import { ReactComponent as LoudspeakerSvg } from 'src/assets/svg/loudspeaker.svg'
import { ReactComponent as SadSvg } from 'src/assets/svg/Sad.svg'
import { ReactComponent as WowSvg } from 'src/assets/svg/Wow.svg'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import { QuestionMarkTooltip } from 'src/components/QuestionMarkTooltip'
import { Skeleton } from 'src/components/Skeleton'
import { numberWithCommas } from 'src/utils/numberFormat'
import styles from './engagementInsightsSection.module.scss'
import {
  EngagementInsightsMockData,
  PhraseItem,
  PlatformType,
  SentimentType,
  SourceData,
  SourceType,
} from '../../pages/ContentAnalysisDetail/mockData/engagementInsightsMockData'

const SENTIMENT_COLORS: Record<SentimentType, string> = {
  negative: '#FF6833',
  neutral: '#C7CED1',
  positive: '#8080FF',
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

const getPlatformLabel = (platform: string): string => {
  if (platform === 'all') return 'All Platform'
  return platformDisplayLabel(platform, capitalize(platform))
}

const getPlatformIcon = (platform: string): ReactNode => {
  if (platform === 'all') return <GlobeSvg />
  return SOCIAL_ICONS[platform] || <GlobeSvg />
}

const SOURCE_LABELS: Record<SourceType, string> = {
  captions: 'In Captions',
  comments: 'In Comments',
}

const REACTION_ICONS: Record<string, ReactNode> = {
  Angry: <AngrySvg />,
  Care: <CareSvg />,
  Haha: <HahaSvg />,
  Like: <LikeSvg />,
  Love: <HeartSvg />,
  Sad: <SadSvg />,
  Wow: <WowSvg />,
}

// Real dimensions from engagementInsightsSection.module.scss. Each .card is a
// FIXED 500px tall, so the cards themselves cannot shift — only the rows inside
// .listContainer are skeletoned. Every list is capped at the top 7 items, so 7
// placeholder rows is the true full-height case.
// Row heights: .phraseItem/.emojiItem/.reactionItem all min-height 40px with a
// 4px .listContainer gap; the inner bars are 32px (emoji) and 28px (reaction).
const LIST_SKELETON_COUNT = 7
const PHRASE_ROW_HEIGHT = 40
const EMOJI_BAR_HEIGHT = 32
const REACTION_BAR_HEIGHT = 28
const COUNT_WIDTH = 56 // .phraseCount / .emojiCount min-width 70 minus the icon

interface EngagementInsightsSectionProps {
  data: EngagementInsightsMockData
  isLoading?: boolean
  platforms?: string[]
}

export const EngagementInsightsSection: FC<EngagementInsightsSectionProps> = ({
  data,
  isLoading = false,
  platforms: platformsProp,
}) => {
  const platformList: PlatformType[] = useMemo(
    () =>
      platformsProp && platformsProp.length > 0
        ? (['all', ...platformsProp] as PlatformType[])
        : (['all', 'facebook', 'instagram', 'tiktok'] as PlatformType[]),
    [platformsProp]
  )
  // Global platform filter
  const [globalPlatform, setGlobalPlatform] = useState<PlatformType>('all')
  const [showGlobalPlatformDropdown, setShowGlobalPlatformDropdown] =
    useState(false)

  // Phrases filters
  const [phrasesPlatform, setPhrasesPlatform] = useState<PlatformType>('all')
  const [phrasesSource, setPhrasesSource] = useState<SourceType>('comments')
  const [showPhrasesPlatformDropdown, setShowPhrasesPlatformDropdown] =
    useState(false)
  const [showPhrasesSourceDropdown, setShowPhrasesSourceDropdown] =
    useState(false)

  // Emojis filters
  const [emojisPlatform, setEmojisPlatform] = useState<PlatformType>('all')
  const [emojisSource, setEmojisSource] = useState<SourceType>('comments')
  const [showEmojisPlatformDropdown, setShowEmojisPlatformDropdown] =
    useState(false)
  const [showEmojisSourceDropdown, setShowEmojisSourceDropdown] =
    useState(false)

  // Reactions filters
  const [reactionsPlatform, setReactionsPlatform] =
    useState<PlatformType>('all')
  const [showReactionsPlatformDropdown, setShowReactionsPlatformDropdown] =
    useState(false)

  const wrapperRef = useRef<HTMLDivElement>(null)

  // Close all dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowGlobalPlatformDropdown(false)
        setShowPhrasesPlatformDropdown(false)
        setShowPhrasesSourceDropdown(false)
        setShowEmojisPlatformDropdown(false)
        setShowEmojisSourceDropdown(false)
        setShowReactionsPlatformDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Effective platforms (global overrides local when not 'all')
  const effectivePhrasesPlatform =
    globalPlatform !== 'all' ? globalPlatform : phrasesPlatform
  const effectiveEmojisPlatform =
    globalPlatform !== 'all' ? globalPlatform : emojisPlatform
  const effectiveReactionsPlatform =
    globalPlatform !== 'all' ? globalPlatform : reactionsPlatform

  // Normalize phrases data: read from 'phrases', items may use 'keyword' field name
  const phrasesData: SourceData<PhraseItem> | undefined = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = data.phrases as any
    if (!raw) return undefined
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mapSource = (source: Record<string, any[]>) => {
      const result: Record<string, PhraseItem[]> = {}
      Object.keys(source).forEach((key) => {
        result[key] = (source[key] || []).map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (item: any) => ({
            count: item.count,
            phrase: item.phrase || item.keyword,
            sentiment: item.sentiment,
          })
        )
      })
      return result
    }
    return {
      captions: mapSource(raw.captions || {}),
      comments: mapSource(raw.comments || {}),
    } as unknown as SourceData<PhraseItem>
  }, [data.phrases])

  // Filtered data (limited to top 7, fallback to 'all' if platform missing)
  const filteredPhrases = useMemo(
    () =>
      phrasesData?.[phrasesSource]
        ? [
            ...(phrasesData[phrasesSource][effectivePhrasesPlatform] ||
              phrasesData[phrasesSource].all ||
              []),
          ]
            .sort((a, b) => b.count - a.count)
            .slice(0, 7)
        : [],
    [phrasesData, phrasesSource, effectivePhrasesPlatform]
  )

  const filteredEmojis = useMemo(
    () =>
      data.emojis?.[emojisSource]
        ? [
            ...(data.emojis[emojisSource][effectiveEmojisPlatform] ||
              data.emojis[emojisSource].all ||
              []),
          ]
            .sort((a, b) => b.count - a.count)
            .slice(0, 7)
        : [],
    [data.emojis, emojisSource, effectiveEmojisPlatform]
  )

  const filteredReactions = useMemo(
    () =>
      data.reactions
        ? [
            ...(data.reactions[effectiveReactionsPlatform] ||
              data.reactions.all ||
              []),
          ]
            .sort((a, b) => b.count - a.count)
            .slice(0, 7)
        : [],
    [data.reactions, effectiveReactionsPlatform]
  )

  // Max counts for bar widths
  const maxPhraseCount = Math.max(...filteredPhrases.map((k) => k.count), 1)
  const maxEmojiCount = Math.max(...filteredEmojis.map((e) => e.count), 1)
  const maxReactionCount = Math.max(...filteredReactions.map((r) => r.count), 1)

  const handleGlobalPlatformSelect = (platform: PlatformType) => {
    setGlobalPlatform(platform)
    setShowGlobalPlatformDropdown(false)
  }

  const handlePhrasesPlatformSelect = (platform: PlatformType) => {
    setPhrasesPlatform(platform)
    setShowPhrasesPlatformDropdown(false)
  }

  const handlePhrasesSourceSelect = (source: SourceType) => {
    setPhrasesSource(source)
    setShowPhrasesSourceDropdown(false)
  }

  const handleEmojisPlatformSelect = (platform: PlatformType) => {
    setEmojisPlatform(platform)
    setShowEmojisPlatformDropdown(false)
  }

  const handleEmojisSourceSelect = (source: SourceType) => {
    setEmojisSource(source)
    setShowEmojisSourceDropdown(false)
  }

  const handleReactionsPlatformSelect = (platform: PlatformType) => {
    setReactionsPlatform(platform)
    setShowReactionsPlatformDropdown(false)
  }

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.header}>
        <div className={styles.titleWrapper}>
          <span className={styles.title}>Engagement Insights</span>
          <QuestionMarkTooltip
            overlay="Insights about engagement across your content"
            placement="top"
          />
        </div>
        <div className={styles.dropdownWrapper}>
          <button
            className={styles.platformDropdown}
            onClick={() =>
              setShowGlobalPlatformDropdown(!showGlobalPlatformDropdown)
            }
          >
            {getPlatformIcon(globalPlatform)}
            {getPlatformLabel(globalPlatform)}
            <ChevronSvg />
          </button>
          {showGlobalPlatformDropdown && (
            <div className={styles.dropdownMenu}>
              {platformList.map((platform) => (
                <button
                  className={styles.dropdownItem}
                  key={platform}
                  onClick={() => handleGlobalPlatformSelect(platform)}
                >
                  {getPlatformIcon(platform)}
                  {getPlatformLabel(platform)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={styles.engagementInsightsSection}>
        <div className={styles.cardsContainer}>
          {/* Top Phrases Card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitleWrapper}>
                <span className={styles.cardTitle}>
                  Top Phrases{' '}
                  {phrasesSource === 'comments' ? 'in Comments' : 'in Captions'}
                </span>
                <QuestionMarkTooltip
                  overlay="Most frequently used phrases"
                  placement="top"
                />
              </div>
            </div>

            <div className={styles.filtersRow}>
              <div className={styles.dropdownWrapper}>
                <button
                  className={styles.filterButton}
                  onClick={() =>
                    setShowPhrasesPlatformDropdown(!showPhrasesPlatformDropdown)
                  }
                >
                  {getPlatformIcon(effectivePhrasesPlatform)}
                  {getPlatformLabel(effectivePhrasesPlatform)}
                  <ChevronSvg />
                </button>
                {showPhrasesPlatformDropdown && (
                  <div className={styles.dropdownMenu}>
                    {platformList.map((platform) => (
                      <button
                        className={styles.dropdownItem}
                        key={platform}
                        onClick={() => handlePhrasesPlatformSelect(platform)}
                      >
                        {getPlatformIcon(platform)}
                        {getPlatformLabel(platform)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className={styles.dropdownWrapper}>
                <button
                  className={styles.filterButton}
                  onClick={() =>
                    setShowPhrasesSourceDropdown(!showPhrasesSourceDropdown)
                  }
                >
                  <LoudspeakerSvg />
                  {SOURCE_LABELS[phrasesSource]}
                  <ChevronSvg />
                </button>
                {showPhrasesSourceDropdown && (
                  <div className={styles.dropdownMenu}>
                    {(['comments', 'captions'] as SourceType[]).map(
                      (source) => (
                        <button
                          className={styles.dropdownItem}
                          key={source}
                          onClick={() => handlePhrasesSourceSelect(source)}
                        >
                          <LoudspeakerSvg />
                          {SOURCE_LABELS[source]}
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className={styles.listContainer}>
              {isLoading &&
                Array.from({ length: LIST_SKELETON_COUNT }).map((_, index) => (
                  <Skeleton
                    height={PHRASE_ROW_HEIGHT}
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    width="100%"
                  />
                ))}
              {filteredPhrases.map((item, index) => (
                <div
                  className={styles.phraseItem}
                  key={`${item.phrase}-${index}`}
                >
                  <div
                    className={styles.phraseBarBackground}
                    style={{
                      backgroundColor: SENTIMENT_COLORS[item.sentiment],
                      width: `${(item.count / maxPhraseCount) * 100}%`,
                    }}
                  />
                  <span
                    className={styles.phraseDot}
                    style={{
                      backgroundColor: SENTIMENT_COLORS[item.sentiment],
                    }}
                  />
                  <span className={styles.phraseText}>{item.phrase}</span>
                  <span className={styles.phraseCount}>
                    {numberWithCommas(item.count)}
                    <span className={styles.loudspeakerIcon}>
                      <LoudspeakerSvg />
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Emojis Card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitleWrapper}>
                <span className={styles.cardTitle}>
                  Top Emojis{' '}
                  {emojisSource === 'comments' ? 'in Comments' : 'in Captions'}
                </span>
                <QuestionMarkTooltip
                  overlay="Most frequently used emojis"
                  placement="top"
                />
              </div>
            </div>

            <div className={styles.filtersRow}>
              <div className={styles.dropdownWrapper}>
                <button
                  className={styles.filterButton}
                  onClick={() =>
                    setShowEmojisPlatformDropdown(!showEmojisPlatformDropdown)
                  }
                >
                  {getPlatformIcon(effectiveEmojisPlatform)}
                  {getPlatformLabel(effectiveEmojisPlatform)}
                  <ChevronSvg />
                </button>
                {showEmojisPlatformDropdown && (
                  <div className={styles.dropdownMenu}>
                    {platformList.map((platform) => (
                      <button
                        className={styles.dropdownItem}
                        key={platform}
                        onClick={() => handleEmojisPlatformSelect(platform)}
                      >
                        {getPlatformIcon(platform)}
                        {getPlatformLabel(platform)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className={styles.dropdownWrapper}>
                <button
                  className={styles.filterButton}
                  onClick={() =>
                    setShowEmojisSourceDropdown(!showEmojisSourceDropdown)
                  }
                >
                  <LoudspeakerSvg />
                  {SOURCE_LABELS[emojisSource]}
                  <ChevronSvg />
                </button>
                {showEmojisSourceDropdown && (
                  <div className={styles.dropdownMenu}>
                    {(['comments', 'captions'] as SourceType[]).map(
                      (source) => (
                        <button
                          className={styles.dropdownItem}
                          key={source}
                          onClick={() => handleEmojisSourceSelect(source)}
                        >
                          <LoudspeakerSvg />
                          {SOURCE_LABELS[source]}
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className={styles.listContainer}>
              {isLoading &&
                Array.from({ length: LIST_SKELETON_COUNT }).map((_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div className={styles.emojiItem} key={index}>
                    <div className={styles.progressBarContainer}>
                      <Skeleton
                        height={EMOJI_BAR_HEIGHT}
                        radius={4}
                        width="100%"
                      />
                    </div>
                    <Skeleton height={16} width={COUNT_WIDTH} />
                  </div>
                ))}
              {filteredEmojis.map((item, index) => (
                <div
                  className={styles.emojiItem}
                  key={`${item.emoji}-${index}`}
                >
                  <div className={styles.progressBarContainer}>
                    <span className={styles.emoji}>{item.emoji}</span>
                    <div
                      className={styles.progressBarFill}
                      style={{
                        width: `${(item.count / maxEmojiCount) * 100}%`,
                      }}
                    />
                  </div>
                  <span className={styles.emojiCount}>
                    {numberWithCommas(item.count)}
                    <span className={styles.loudspeakerIcon}>
                      <LoudspeakerSvg />
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Reactions Card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitleWrapper}>
                <span className={styles.cardTitle}>Top Reactions on Posts</span>
                <QuestionMarkTooltip
                  overlay="Most common reactions on your posts"
                  placement="top"
                />
              </div>
            </div>

            <div className={`${styles.filtersRow} ${styles.fullWidth}`}>
              <div className={styles.dropdownWrapper}>
                <button
                  className={styles.filterButton}
                  onClick={() =>
                    setShowReactionsPlatformDropdown(
                      !showReactionsPlatformDropdown
                    )
                  }
                >
                  {getPlatformIcon(effectiveReactionsPlatform)}
                  {getPlatformLabel(effectiveReactionsPlatform)}
                  <ChevronSvg />
                </button>
                {showReactionsPlatformDropdown && (
                  <div className={styles.dropdownMenu}>
                    {platformList.map((platform) => (
                      <button
                        className={styles.dropdownItem}
                        key={platform}
                        onClick={() => handleReactionsPlatformSelect(platform)}
                      >
                        {getPlatformIcon(platform)}
                        {getPlatformLabel(platform)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className={styles.listContainer}>
              {isLoading &&
                Array.from({ length: LIST_SKELETON_COUNT }).map((_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div className={styles.reactionItem} key={index}>
                    <div className={styles.reactionBarContainer}>
                      <Skeleton
                        height={REACTION_BAR_HEIGHT}
                        radius={4}
                        width="100%"
                      />
                    </div>
                    <Skeleton height={16} width={COUNT_WIDTH} />
                  </div>
                ))}
              {filteredReactions.map((item, index) => (
                <div
                  className={styles.reactionItem}
                  key={`${item.name}-${index}`}
                >
                  <div className={styles.reactionBarContainer}>
                    <div
                      className={styles.reactionBarFill}
                      style={{
                        width: `${(item.count / maxReactionCount) * 100}%`,
                      }}
                    />
                    <span className={styles.reactionIcon}>
                      {REACTION_ICONS[item.name] || item.emoji}
                    </span>
                    <span className={styles.reactionName}>{item.name}</span>
                  </div>
                  <span className={styles.reactionCount}>
                    {numberWithCommas(item.count)}
                    <span className={styles.loudspeakerIcon}>
                      <LoudspeakerSvg />
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
